/**
 * Varejo · Carteira Viva (#2, 10/08) — valuation da carteira VIVA do Zeus,
 * ao lado da réplica do Estudo (ValuationTab). Decisões Veiga 10/08:
 *   D1 — EG precificado por TARIFA−DESCONTO (nunca PF-like); sem tarifa
 *        computável o contrato vira EXCEÇÃO com vol+valor (nunca estimado).
 *   D2 — HEADLINE = CARTEIRA FUTURA; mês realizado = histórico declarado.
 *   D3 — desconto = CDI (curva pré) + SPREAD editável que RECALCULA (live);
 *        default 0 = "CDI flat" (prêmio de risco é ato do usuário).
 *
 * TRAVAS: o Estudo MTM NÃO alimenta nenhum número desta tela; componente que
 * não computa DIZ que falhou (fail-closed — nunca número parcial com cara de
 * completo); premissas DECLARADAS aqui (payload → tela, não doc).
 * As-of VISÍVEL (#60): snapshot precomputado × recálculo live no badge.
 *
 * SENSÍVEL: razao (nome do cliente) renderiza na tela autenticada, mas NÃO é
 * logado (mesma regra da ValuationTab).
 */
import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Loading } from "../Loading";
import { AsOfBadge } from "../ui/AsOfBadge";
import { useVarejoValuationViva } from "../../hooks/useVarejo";
import { fmtBRLcompact, fmtBRLcompactSigned, fmtMWm } from "../../utils/formatters";
import type { ValuationEscopo } from "../../api/types";

type Modo = "off" | "on";
type EscopoKey = "Total" | "EG" | "PF";

function fmtRsMwh(v: number | null | undefined, dec = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `R$ ${v.toLocaleString("pt-BR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  })}`;
}

function fmtMilMwh(mwh: number): string {
  return `~${Math.round(mwh / 1000).toLocaleString("pt-BR")} mil MWh`;
}

const sgn = (v: number | null | undefined) =>
  v !== null && v !== undefined && v < 0 ? "text-indra-negativo" : "text-indra-positivo";

export function CarteiraVivaTab() {
  // spread APLICADO (o que a query usa) × campo em edição — recalcular é ato
  // explícito (botão), não side-effect de digitação.
  const [spread, setSpread] = useState(0);
  const [campo, setCampo] = useState("0");
  const { data, isLoading, error } = useVarejoValuationViva(spread);
  const [modo, setModo] = useState<Modo>("off");

  if (isLoading && !data) return <Loading label="Carregando Carteira Viva…" />;
  if (error || !data) {
    // TRAVA 3: fail-closed — a tela DIZ que não computou; zero número parcial.
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
        <div className="flex items-center gap-2 font-semibold">
          <AlertTriangle size={16} /> Carteira Viva NÃO computada
        </div>
        <p className="mt-1">
          O backend não devolveu a valuation ({error?.message ?? "sem payload"}).
          Nenhum número é mostrado — parcial com cara de completo é pior que
          ausência. Tente recarregar; persistindo, ver /api/varejo/valuation?fonte=viva.
        </p>
      </div>
    );
  }

  const md = data.metadados;
  const escopo = (k: EscopoKey): ValuationEscopo => data[modo][k];
  const tot = escopo("Total");
  const hist = md.historico_realizado;
  const excecoes = md.excecoes ?? [];
  const volExc = md.vol_excluido_mwh ?? 0;
  // #94: 3 estados derivados dos CONTADORES do dash_diag (o booleano
  // dash_tarifas_disponivel foi aposentado — media "run sem excecao" e
  // mentiu nos 2 sentidos). Fallback pro booleano so em snapshot antigo.
  const diag = md.dash_diag ?? null;
  const nFalhas = diag?.chamadas_falha
    ? Object.values(diag.chamadas_falha).reduce((s, v) => s + (v ?? 0), 0)
    : 0;
  const temContadores = diag?.chamadas_falha != null;
  const dashOff = temContadores
    ? (diag?.chamadas_ok ?? 0) === 0 && nFalhas > 0
    : md.dash_tarifas_disponivel === false;
  const dashParcial = temContadores && !dashOff && nFalhas > 0;
  const contratos = data.por_contrato.slice(0, 12);
  // (1) 10/08: HEADLINE PARCIAL declarado NO CARD — condição no DADO (some
  // sozinha quando o EG voltar a precificar), nunca flag manual. Doutrina da
  // tarja/card 6DU: número possivelmente enganoso não sai liso.
  const excEg = excecoes.filter((e) => e.motivo.startsWith("eg_sem_tarifa"));
  const egVolExc = excEg.reduce((s, e) => s + e.vol_mwh, 0);
  const egContratosExc = new Set(excEg.map((e) => e.id_contrato)).size;
  const headlineParcial = egVolExc > 0;

  function aplicarSpread() {
    const v = Number(campo.replace(",", "."));
    if (Number.isFinite(v) && v >= -50 && v <= 50) setSpread(v);
  }

  return (
    <div className="space-y-6">
      {/* header: headline futura + as-of visível (#60) */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">
            Carteira Viva — headline = carteira FUTURA
          </h3>
          <p className="text-xs text-gray-500">
            Data Carteira {data.data_carteira} · {md.n_contratos} contratos (
            {md.n_contratos_eg} EG / {md.n_contratos_pf} PF) · VP sobre{" "}
            {md.vp_base === "margem_bruta" ? "margem BRUTA" : "margem líquida"}
          </p>
          {/* 31/08: PROXY DECLARADO junto do número — denominador, não alerta
              (doutrina da exclusão #66: estado permanente não cabe em aviso). */}
          {md.eg_proxy_cativo && md.eg_proxy_cativo.n_contratos > 0 && (
            <p className="text-xs italic text-gray-500">
              por proxy: {md.eg_proxy_cativo.n_contratos} de{" "}
              {md.eg_proxy_cativo.n_contratos_eg_precificados} contratos EG (
              {fmtBRLcompact(md.eg_proxy_cativo.valor_rs)}) — subgrupo e
              modalidade lidos do campo LIVRE, cativo vazio
              {/* 01/09: denominador = headline (o mesmo do "N EG" acima);
                  EG precificados já encerrados ficam declarados, não somados */}
              {md.eg_proxy_cativo.fora_headline &&
                md.eg_proxy_cativo.fora_headline.n_contratos > 0 && (
                  <>
                    {" "}· {md.eg_proxy_cativo.fora_headline.n_contratos} EG
                    encerrado(s) fora do headline (
                    {fmtBRLcompact(md.eg_proxy_cativo.fora_headline.valor_rs)})
                  </>
                )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <AsOfBadge asOf={data.as_of ?? null} />
          <div className="flex rounded-md border text-xs">
            {(["off", "on"] as Modo[]).map((m) => (
              <button
                key={m}
                onClick={() => setModo(m)}
                className={`px-3 py-1 ${modo === m ? "bg-gray-800 text-white" : ""}`}
              >
                {m === "off" ? "Nominal" : "IPCA on"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(dashOff || headlineParcial) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle size={14} className="mr-1 inline" />
          {dashOff
            ? "Dash de tarifas INDISPONÍVEL neste snapshot — sob a regra D1 " +
              "(EG = tarifa−desconto, nunca estimado) a carteira EG inteira " +
              "está nas EXCEÇÕES, fora do headline."
            : `EG NÃO precificado (${fmtMilMwh(egVolExc)}, ${egContratosExc} ` +
              "contratos sem tarifa computável) — headline PARCIAL até a " +
              "tarifa voltar; detalhe nas exceções." +
              (dashParcial
                ? ` Atenção: ${nFalhas} falha(s) de chamada ao dash neste ` +
                  "snapshot — parte das exceções pode ser indisponibilidade, " +
                  "não tarifa inexistente."
                : "")}
        </div>
      )}

      {/* D3: desconto CDI + spread que RECALCULA */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm">
        <span className="font-medium">Desconto: CDI (curva pré)</span>
        <span className="text-gray-500">+ spread</span>
        <input
          data-testid="spread-input"
          value={campo}
          onChange={(e) => setCampo(e.target.value)}
          className="w-20 rounded border px-2 py-1 text-right"
          inputMode="decimal"
        />
        <span className="text-gray-500">p.p. a.a.</span>
        <button
          data-testid="spread-aplicar"
          onClick={aplicarSpread}
          className="rounded bg-gray-800 px-3 py-1 text-xs text-white"
        >
          Recalcular
        </button>
        <span className="text-xs text-gray-500">
          aplicado: {md.spread_aa ?? 0} p.p. —{" "}
          {(md.spread_aa ?? 0) === 0
            ? "CDI FLAT (sem prêmio de risco embutido)"
            : "com prêmio de risco EXPLÍCITO do usuário"}
        </span>
      </div>

      {/* cards Total / EG / PF */}
      <div className="grid gap-4 md:grid-cols-3">
        {(["Total", "EG", "PF"] as EscopoKey[]).map((k) => {
          const e = escopo(k);
          const parcialAqui = headlineParcial && (k === "Total" || k === "EG");
          return (
            <div key={k} className="rounded-lg border p-4">
              <div className="text-xs uppercase text-gray-500">
                {k === "Total" ? "Carteira futura" : md.labels_modalidade[k as "EG" | "PF"]}
              </div>
              <div className={`text-2xl font-semibold ${sgn(e.mb)}`}>
                {fmtBRLcompactSigned(e.mb)}
              </div>
              <div className="text-xs text-gray-500">margem bruta nominal</div>
              <div className={`mt-2 text-lg ${sgn(e.vp)}`}>
                VP {e.vp === null ? "—" : fmtBRLcompactSigned(e.vp)}
              </div>
              <div className="text-xs text-gray-500">
                {fmtMilMwh(e.vol_mwh)} · {fmtMWm(e.vol_mwm)}
              </div>
              {parcialAqui && (
                <div
                  data-testid={`parcial-${k}`}
                  className="mt-2 rounded bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800"
                >
                  parcial: EG não precificado ({fmtMilMwh(egVolExc)},{" "}
                  {egContratosExc} contratos)
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* D2: histórico realizado DECLARADO à parte */}
      {hist && hist.n_meses > 0 && (
        <p className="text-xs text-gray-600">
          Histórico realizado (fora do headline): MB{" "}
          {fmtBRLcompactSigned(hist.mb)} em {hist.n_meses} mês(es) até{" "}
          {hist.ate ?? "—"} ({fmtMilMwh(hist.vol_mwh)}) — {hist.nota}
        </p>
      )}

      {/* por vértice (Total, modo ativo) */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-gray-500">
              <th className="py-1 pr-4">Ano</th>
              <th className="py-1 pr-4">Vol</th>
              <th className="py-1 pr-4">Fwd atual</th>
              <th className="py-1 pr-4">VP contrib</th>
              <th className="py-1 pr-4">Δ empate</th>
            </tr>
          </thead>
          <tbody>
            {tot.por_vertice.map((v) => (
              <tr key={v.ano} className="border-b last:border-0">
                <td className="py-1 pr-4">{v.ano}</td>
                <td className="py-1 pr-4">{fmtMilMwh(v.vol_mwh)}</td>
                <td className="py-1 pr-4">{fmtRsMwh(v.fwd_atual_rs_mwh)}</td>
                <td className={`py-1 pr-4 ${sgn(v.vp_contrib)}`}>
                  {v.vp_contrib === null ? "—" : fmtBRLcompact(v.vp_contrib)}
                </td>
                <td className="py-1 pr-4">
                  {v.delta_empate_rs_mwh === null ? "—" : fmtRsMwh(v.delta_empate_rs_mwh, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* drill: piores contratos primeiro (MB asc — ML pendente) */}
      <details>
        <summary className="cursor-pointer text-sm font-medium">
          Contratos (12 piores por MB)
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-4">Contrato</th>
                <th className="py-1 pr-4">Cliente</th>
                <th className="py-1 pr-4">Tipo</th>
                <th className="py-1 pr-4">MB</th>
                <th className="py-1 pr-4">VP</th>
              </tr>
            </thead>
            <tbody>
              {contratos.map((c) => (
                <tr key={`${c.id_cliente}-${c.tipo_contrato}`} className="border-b last:border-0">
                  <td className="py-1 pr-4">{c.id_cliente}</td>
                  <td className="py-1 pr-4">{c.razao ?? "—"}</td>
                  <td className="py-1 pr-4">{c.tipo_contrato}</td>
                  <td className={`py-1 pr-4 ${sgn(c.mb)}`}>{fmtBRLcompactSigned(c.mb)}</td>
                  <td className={`py-1 pr-4 ${sgn(c.vp)}`}>
                    {c.vp === null ? "—" : fmtBRLcompactSigned(c.vp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* TRAVA 2: exceções com vol E valor — o que não marca APARECE */}
      <details open={excecoes.length > 0}>
        <summary className="cursor-pointer text-sm font-medium">
          Exceções — fora do headline ({excecoes.length} grupos,{" "}
          {fmtMilMwh(volExc)})
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-4">Contrato</th>
                <th className="py-1 pr-4">Cliente</th>
                <th className="py-1 pr-4">Motivo</th>
                <th className="py-1 pr-4">Vol (MWh)</th>
                <th className="py-1 pr-4">Valor</th>
              </tr>
            </thead>
            <tbody>
              {excecoes.map((e, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1 pr-4">{e.id_contrato ?? "—"}</td>
                  <td className="py-1 pr-4">{e.razao ?? "—"}</td>
                  <td className="py-1 pr-4">{e.motivo}</td>
                  <td className="py-1 pr-4">
                    {e.vol_mwh.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1 pr-4">
                    {e.valor_rs === undefined ? "—" : fmtBRLcompact(e.valor_rs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* premissas DECLARADAS (payload → tela) */}
      <details>
        <summary className="cursor-pointer text-sm font-medium">Premissas</summary>
        <ul className="mt-2 list-disc pl-5 text-xs text-gray-600">
          {md.premissas_congeladas.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
